import { useEffect, useRef } from "react"
import type { ReactNode } from "react"

import type { VoicePresenceTag } from "./voice-stage-utils"
import type { VoiceRingState } from "../voice-activity-ring-utils"

import { NodePill } from "@/components/ui/node-pill"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { cn } from "@/lib/utils"

import { VoiceMorph } from "./voice-morph"
import { useHasChanged } from "./voice-morph-utils"
import styles from "./voice-stage.module.css"

type VoicePresenceProps = {
  ringState: VoiceRingState
  tag: VoicePresenceTag
  /** The one word shown, already translated. */
  label: string
  icon: ReactNode
  analyserNode?: AnalyserNode | null
}

const TONE_CLASS: Record<VoicePresenceTag["tone"], string | undefined> = {
  outlined: undefined,
  primary: styles.pillPrimary,
  warning: styles.pillWarning,
  quiet: styles.pillQuiet,
  destructive: styles.pillDestructive,
}

/**
 * The presence: a brand tag naming the state, with two echo rings around it.
 * The rings are soft and blurred in silence and sharpen when someone speaks;
 * while a voice is live they follow its level, written to a CSS property from
 * the analyser so the visual reacts without a React render per frame.
 *
 * A state change morphs the word and its icon (Focus) and sends one ripple
 * through the rings; the pill's width and tone follow.
 */
export function VoicePresence({ ringState, tag, label, icon, analyserNode }: VoicePresenceProps) {
  const ref = useRef<HTMLDivElement>(null)
  const smoothedRef = useRef(0.08)
  const reducedMotion = usePrefersReducedMotion()
  // The window opens in a state; only leaving it is worth a ripple.
  const rippling = useHasChanged(ringState)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    if (!analyserNode || reducedMotion) {
      // No live level to write: hand the rings back to the CSS stand-in.
      delete element.dataset.level
      element.style.removeProperty("--voice-level")
      return
    }
    // Between voices the last level stays where it is. The rings' weight for it
    // eases to nothing, so taking the level away here would show as a jump.
    if (tag.voice === null) return

    // An animation outranks an inline value, so the stand-in has to stand down
    // for the analyser's level to reach the rings at all.
    element.dataset.level = "live"

    const bins = new Uint8Array(analyserNode.frequencyBinCount)
    let frame: number | null = null
    const draw = () => {
      analyserNode.getByteFrequencyData(bins)
      const useful = Math.max(1, Math.floor(bins.length * 0.62))
      let energy = 0
      for (let index = 0; index < useful; index += 1) {
        energy += Math.pow(bins[index] / 255, 1.45)
      }
      const target = 0.05 + (energy / useful) * 0.95
      const previous = smoothedRef.current
      const smoothed = previous + (target - previous) * (target > previous ? 0.34 : 0.12)
      smoothedRef.current = smoothed
      element.style.setProperty("--voice-level", smoothed.toFixed(3))
      frame = window.requestAnimationFrame(draw)
    }
    frame = window.requestAnimationFrame(draw)

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [analyserNode, reducedMotion, tag.voice])

  return (
    <div
      ref={ref}
      aria-label={label}
      className={styles.presence}
      data-state={ringState}
      data-testid="voice-presence"
      data-voice={tag.voice ?? undefined}
      role="img"
    >
      <span aria-hidden="true" className={cn(styles.echo, styles.echo2)} />
      <span aria-hidden="true" className={cn(styles.echo, styles.echo1)} />
      {/* One ripple per state change, on its own element so it never fights
          the echoes' own animation for this state. */}
      {rippling && <span key={ringState} aria-hidden="true" className={styles.echoRipple} />}
      <NodePill
        brand
        data-tone={tag.tone}
        className={cn(
          styles.core,
          styles.pill,
          TONE_CLASS[tag.tone],
          tag.tone === "quiet" && styles.pillDashed
        )}
      >
        {/* The word and its icon are one face: a state change blurs the old
            one out and sharpens the new one in (Focus), and the pill eases to
            the new width. The test id rides the current face, never a ghost. */}
        <VoiceMorph data-testid="voice-presence-word" id={label} mode="focus">
          {label}
          {icon}
        </VoiceMorph>
      </NodePill>
    </div>
  )
}
